/**
 * ActiveAgentsPage — /agents/active
 * Demo pages: /ai (run status, cost, health as summary numbers) and /tables/data-tables (list, status pills, row ⋯ menu).
 * Spawn and terminate call routes that need CanManageAgents; viewing needs CanViewAgents or CanManageAgents.
 */
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Activity, CheckCircle2, CircleDollarSign, Heart, Plus, RefreshCw, XCircle, Bot } from 'lucide-react';
import {
  ActionMenu,
  Badge,
  Button,
  Callout,
  ConfirmDialog,
  DataTable,
  EmptyState,
  FilterPills,
  SearchBar,
  Toolbar,
  useToast,
  type BadgeVariant,
  type DataTableColumn,
} from '../../components/ui/index.js';
import { ListPage } from '../../components/page/index.js';
import { Can } from '../../components/auth/Can.js';
import { describeQueryError } from '../../components/ui/QueryBoundary.js';
import { PAGE_HELP } from '../../help/index.js';
import {
  listActiveAgentsOptions,
  listActiveAgentsQueryKey,
  terminateAgentMutation,
} from '../../generated/api/@tanstack/react-query.gen.js';
import type { AgentSummary } from '../../generated/api/types.gen.js';
import { sentenceCase } from '../../components/ui/FilterSelect.js';
import { SpawnAgentModal } from './active-agents/SpawnAgentModal.js';

const STATUS_VARIANT: Record<string, BadgeVariant> = {
  idle: 'success',
  running: 'accent',
  waiting: 'warning',
  error: 'danger',
  paused: 'neutral',
  terminated: 'neutral',
};

function healthTone(score: number): BadgeVariant {
  if (score > 0.7) return 'success';
  if (score > 0.3) return 'warning';
  return 'danger';
}

export const ActiveAgentsPage: React.FC = () => {
  const help = PAGE_HELP['active-agents'];
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [spawnOpen, setSpawnOpen] = useState(false);
  const [terminating, setTerminating] = useState<AgentSummary | null>(null);

  const query = useQuery(listActiveAgentsOptions());
  const terminate = useMutation({
    ...terminateAgentMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: listActiveAgentsQueryKey() });
      notify('Agent terminated', 'success');
      setTerminating(null);
    },
    onError: (error) => {
      setTerminating(null);
      notify(error.message, 'danger');
    },
  });

  const agents = (query.data?.data ?? []).filter((a) => a.status !== 'terminated');
  const term = search.trim().toLowerCase();
  const shown = agents.filter(
    (a) =>
      (status === 'all' || a.status === status) &&
      (term === '' || a.name.toLowerCase().includes(term) || a.role.toLowerCase().includes(term)),
  );
  const running = agents.filter((a) => a.status === 'running').length;
  const errored = agents.filter((a) => a.status === 'error').length;
  const tasksDone = agents.reduce((sum, a) => sum + a.tasksCompleted, 0);
  const cost = agents.reduce((sum, a) => sum + a.totalCostUSD, 0);
  const statusIds = [...new Set(agents.map((a) => a.status))].sort();

  const refresh = () => queryClient.invalidateQueries({ queryKey: listActiveAgentsQueryKey() });

  const columns: DataTableColumn<AgentSummary>[] = [
    {
      key: 'agent',
      header: 'Agent',
      width: '28%',
      sortable: true,
      sortValue: (a) => a.name.toLowerCase(),
      render: (a) => (
        <div>
          <div className="font-medium text-heading">{a.name}</div>
          <div className="text-xs text-muted">{a.model}</div>
        </div>
      ),
    },
    { key: 'role', header: 'Role', render: (a) => <span className="text-sm text-muted">{a.role}</span> },
    {
      key: 'status',
      header: 'Status',
      render: (a) => (
        <Badge variant={STATUS_VARIANT[a.status] ?? 'neutral'} dot>
          {sentenceCase(a.status)}
        </Badge>
      ),
    },
    {
      key: 'health',
      header: 'Health',
      sortable: true,
      sortValue: (a) => a.healthScore,
      render: (a) => (
        <Badge variant={healthTone(a.healthScore)} icon={<Heart size={12} aria-hidden="true" />}>
          {Math.round(a.healthScore * 100)}%
        </Badge>
      ),
    },
    {
      key: 'tasks',
      header: 'Tasks done',
      align: 'right',
      sortable: true,
      sortValue: (a) => a.tasksCompleted,
      render: (a) => <span className="text-sm text-body">{a.tasksCompleted}</span>,
    },
    {
      key: 'cost',
      header: 'Cost',
      align: 'right',
      sortable: true,
      sortValue: (a) => a.totalCostUSD,
      render: (a) => <span className="text-sm text-body">${a.totalCostUSD.toFixed(4)}</span>,
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '56px',
      render: (a) => (
        <Can policy="CanManageAgents">
          <ActionMenu
            aria-label={`Actions for ${a.name}`}
            items={[
              {
                id: 'terminate',
                label: 'Terminate',
                icon: <XCircle size={14} aria-hidden="true" />,
                tone: 'danger',
                onAction: () => setTerminating(a),
              },
            ]}
          />
        </Can>
      ),
    },
  ];

  return (
    <>
      <ListPage
        title="Active agents"
        subtitle="Agent instances running now: their status, health, tasks and cost"
        helpContent={help}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={refresh}>
              <RefreshCw size={14} aria-hidden="true" />
              Refresh
            </Button>
            <Can policy="CanManageAgents">
              <Button size="sm" onClick={() => setSpawnOpen(true)}>
                <Plus size={14} aria-hidden="true" />
                Spawn agent
              </Button>
            </Can>
          </div>
        }
        stats={[
          { label: 'Active', value: agents.length, icon: Bot },
          { label: 'Running', value: running, icon: Activity, severity: running > 0 ? 'accent' : undefined },
          { label: 'In error', value: errored, icon: XCircle, severity: errored > 0 ? 'danger' : undefined },
          { label: 'Tasks done', value: tasksDone, icon: CheckCircle2 },
          { label: 'Cost', value: `$${cost.toFixed(4)}`, icon: CircleDollarSign },
        ]}
        alert={
          query.isError ? (
            <Callout
              tone="danger"
              title={describeQueryError(query.error, 'the active agents')}
              actions={
                <Button size="sm" variant="secondary" onClick={() => query.refetch()}>
                  Retry
                </Button>
              }
            />
          ) : undefined
        }
        toolbar={
          <Toolbar
            search={<SearchBar value={search} onChange={setSearch} placeholder="Search by name or role…" />}
            pills={
              statusIds.length > 1 ? (
                <FilterPills
                  aria-label="Agent status"
                  value={status}
                  onChange={setStatus}
                  options={[
                    { id: 'all', label: 'All', count: agents.length },
                    ...statusIds.map((id) => ({
                      id,
                      label: sentenceCase(id),
                      count: agents.filter((a) => a.status === id).length,
                    })),
                  ]}
                />
              ) : undefined
            }
          />
        }
      >
        <DataTable
          aria-label="Active agents"
          columns={columns}
          rows={shown}
          rowKey={(a) => a.id}
          loading={query.isPending}
          getRowAttention={(a) => (a.status === 'error' ? 'danger' : undefined)}
          empty={
            <EmptyState
              icon={Activity}
              title={term || status !== 'all' ? 'No agents match' : help.emptyState.title}
              description={
                term || status !== 'all' ? 'Change the search or clear the status filter.' : help.emptyState.description
              }
              learnItems={term || status !== 'all' ? undefined : help.emptyState.learnItems}
            />
          }
        />
      </ListPage>

      <Can policy="CanManageAgents">
        <SpawnAgentModal
          open={spawnOpen}
          onClose={() => setSpawnOpen(false)}
          onSpawned={() => {
            setSpawnOpen(false);
            refresh();
          }}
        />
        <ConfirmDialog
          open={terminating !== null}
          onClose={() => setTerminating(null)}
          onConfirm={() =>
            terminating &&
            terminate.mutate({ path: { agentId: terminating.id }, body: { reason: 'Manual termination from UI' } })
          }
          title="Terminate this agent?"
          message={`${terminating?.name ?? 'The agent'} stops immediately and any task it is running is interrupted.`}
          confirmLabel="Terminate agent"
          loading={terminate.isPending}
        />
      </Can>
    </>
  );
};
